import { WorkItem } from '../api/workItems';
import { formatHumanDrawerDate } from './deriveNextStep';
import { getWorkItemSourceInfo, getWorkItemStateLabel, WorkItemSourceKind } from './workItemDisplay';

/**
 * List position in the drawer. The pile name is part of the sentence so a
 * fraction never sits alone beside a title (that reads as children of one source).
 * Returns null when the pile is missing — never a bare "N of M".
 */
export function formatListPosition(position: number, total: number, pile: string): string | null {
    const name = pile.trim();
    if (!name) return null;
    if (!Number.isInteger(position) || !Number.isInteger(total)) return null;
    if (position < 1 || total < 2 || position > total) return null;
    return `${position} of ${total} in ${name}`;
}

/**
 * Real parent → child edge. Only email or document, and only when more than
 * one task was actually created from that parent. The drawer replaces this
 * stored count with the number of steps the family endpoint can open.
 */
export function formatDerivedTasks(kind: 'email' | 'document', count: number): string | null {
    if (!Number.isInteger(count) || count <= 1) return null;
    const noun = kind === 'email' ? 'email' : 'document';
    return `From this ${noun} · ${count} tasks`;
}

function readChildCount(meta: WorkItem['meta']): number | null {
    if (!meta || typeof meta !== 'object') return null;
    const record = meta as Record<string, unknown>;
    const batchId = record.batch_id;
    if (typeof batchId !== 'string' || !batchId.trim()) return null;
    const raw = record.batch_total;
    const parsed = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;
    if (!Number.isFinite(parsed)) return null;
    const count = Math.floor(parsed);
    return count > 1 ? count : null;
}

/** Derived-work sentence, or null when this item has no real parent edge. */
export function derivedTasksLabel(
    item: Pick<WorkItem, 'source' | 'gmail_message_id' | 'document_id' | 'meta' | 'source_lineage'>,
): string | null {
    const count = readChildCount(item.meta);
    if (count == null) return null;
    const sourceType = getWorkItemSourceInfo(item).sourceType;
    if (sourceType !== 'email' && sourceType !== 'document') return null;
    return formatDerivedTasks(sourceType, count);
}

/** Today prev/next walks the whole board. Name a column only when it is the entire list. */
export function todayBoardPileLabel(counts: {
    now: number;
    next: number;
    triage: number;
    later: number;
}): string {
    const named = [
        counts.now > 0 ? 'Priority Now' : '',
        counts.next > 0 ? 'Up Next' : '',
        counts.triage > 0 ? 'Triage' : '',
        counts.later > 0 ? 'Later' : '',
    ].filter(Boolean);
    if (named.length === 1) return named[0];
    return 'Today';
}

/** Short chip above the title. Unknown sources stay unlabeled. */
export function sourceKindChipLabel(sourceType: WorkItemSourceKind): string | null {
    if (sourceType === 'email') return 'Email';
    if (sourceType === 'document') return 'Document';
    if (sourceType === 'manual') return 'Note';
    return null;
}

/** Life-area chip. Raw keys become words so `job_hunt` never reaches the header. */
export function lifeAreaChipLabel(label: string | null | undefined): string | null {
    const raw = (label || '').trim();
    if (!raw) return null;
    if (!/^[a-z0-9_]+$/.test(raw)) return raw;
    return raw
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

const FORWARDED_SUMMARY = /^\s*(?:-+\s*(?:forwarded|original) message|begin forwarded message:|from:|sent from my\b)/i;

/**
 * The item's stored or edited summary, not the original source body.
 * A forwarded-header dump is not a summary. Returns null rather than a placeholder.
 */
export function sourceSummaryText(summary: string | null | undefined): string | null {
    const text = (summary || '').trim();
    if (!text || FORWARDED_SUMMARY.test(text)) return null;
    return text;
}

/** Notes that are only `From: …` are a real sender, not a second summary. */
export function readSenderFromNotes(notes: string | null | undefined): string | null {
    const raw = (notes || '').trim();
    if (!raw || raw.includes('\n')) return null;
    const match = raw.match(/^from:\s*(.+)$/i);
    const sender = match?.[1]?.trim() ?? '';
    return sender || null;
}

/**
 * One quiet provenance sentence. No clock unless we already store one that
 * means "when this arrived" — we do not, so the time is omitted.
 */
export function quietProvenanceFact(input: {
    createdFrom: string;
    sender?: string | null;
    documentFilename?: string | null;
}): string | null {
    const created = input.createdFrom.trim();
    if (!created || created === 'Source unknown') return null;
    const sender = (input.sender || '').trim();
    if (sender) return `${created} · ${sender}`;
    const filename = (input.documentFilename || '').trim();
    if (filename && created === 'From document') return `From document · ${filename}`;
    return created;
}

/**
 * The state half of the status line. A future snooze on open work says so (D3); done
 * and archived keep their word, because a snooze nobody cleared changes nothing there.
 */
export function drawerStatusLabel(
    item: Pick<WorkItem, 'state' | 'snoozed_until'> | null | undefined,
    now: Date = new Date(),
): string | null {
    const state = (item?.state || '').toUpperCase();
    const stateLabel = getWorkItemStateLabel(state) || state || null;
    if (!item?.snoozed_until || state === 'DONE' || state === 'ARCHIVED') return stateLabel;
    const until = new Date(item.snoozed_until);
    if (Number.isNaN(until.getTime()) || until <= now) return stateLabel;
    const date = formatHumanDrawerDate(item.snoozed_until, now);
    return date ? `Snoozed until ${date}` : stateLabel;
}

/** Due phrase plus state label. Either half may be missing. */
export function dueStatusLine(duePhrase: string | null | undefined, statusLabel: string | null | undefined): string | null {
    const due = (duePhrase || '').trim();
    const status = (statusLabel || '').trim();
    if (due && status) return `${due} · ${status}`;
    return due || status || null;
}

/** Workbench prev/next walks the filtered list. Name that list, not the item's state chip. */
export function workbenchListPileLabel(input: {
    view: 'inbox' | 'applications' | 'tasks' | 'all';
    stateFilter: string;
    archive?: boolean;
}): string {
    if (input.archive || input.stateFilter === 'ARCHIVED') return 'Archive';
    if (input.view === 'inbox') return 'Inbox';
    if (input.view === 'applications') return 'Applications';
    if (input.view === 'tasks') return 'Tasks';
    const state = input.stateFilter.trim().toUpperCase();
    if (state === 'TRIAGE') return 'Triage';
    if (state === 'ACTIVE') return 'Open';
    if (state === 'DONE') return 'Done';
    if (state === 'ALL') return 'All items';
    return 'Open';
}
